import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import MDBox from "components/MDBox";
import MDAvatar from "components/MDAvatar";
import burceMars from "assets/images/logos/Banyan.jpg";
import { useNavigate } from "react-router-dom";
import { FaEdit, FaShare } from "react-icons/fa";
import { useSelector, useDispatch } from "react-redux";
import Typography from "@mui/material/Typography";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import CloseIcon from "@mui/icons-material/Close";
import Box from "@mui/material/Box";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import Webcam from "react-webcam";
import TextField from "@mui/material/TextField";
import LinearProgress from "@mui/material/LinearProgress";
import Alert from "@mui/material/Alert";
import { setUserPhoto } from "../../../../Redux/action/sessionAction";
import MDSnackbar from "components/MDSnackbar";
import ShareProfileDialog from "components/ShareProfileDialog";

const REMOVED_IMAGE_SENTINEL = "__PROFILE_IMAGE_REMOVED__";
const MAX_CAMERA_RETRIES = 4;

const readFileAsDataUrl = (file) =>
  new Promise((resolve, reject) => {
    if (!file) {
      resolve("");
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      resolve(typeof reader.result === "string" ? reader.result : "");
    };
    reader.onerror = () => {
      reject(reader.error || new Error("Failed to read image file"));
    };
    reader.readAsDataURL(file);
  });

function Header({ children, profile, onEditClick, educationDetails = [], workDetails = [], idCardDetails = [], bankDetails = [], addressDetails = [] }) {
  const [profileImage, setProfileImage] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState(null);
  const [openImageDialog, setOpenImageDialog] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [cameraDialogOpen, setCameraDialogOpen] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  const webcamRef = useRef(null);
  const triedDevicesRef = useRef(new Set());
  const [facingMode, setFacingMode] = useState("user");
  const [cameraError, setCameraError] = useState(null);
  const [cameraReady, setCameraReady] = useState(false);
  const [webcamKey, setWebcamKey] = useState(-1);
  const [cameraRetryCount, setCameraRetryCount] = useState(0);
  const [cameraMessage, setCameraMessage] = useState(null);
  const [videoDevices, setVideoDevices] = useState([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState(null);
  const [loosenConstraints, setLoosenConstraints] = useState(false);
  const [forceDefaultDevice, setForceDefaultDevice] = useState(false);
  const [useBareConstraints, setUseBareConstraints] = useState(false);
  const [successSB, setSuccessSB] = useState(false);
  const [errorSB, setErrorSB] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [shareOpen, setShareOpen] = useState(false);

  // Auto-hide success toast after 3 seconds
  useEffect(() => {
    if (successSB) {
      const timer = setTimeout(() => {
        setSuccessSB(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [successSB]);

  // Auto-hide error toast after 3 seconds
  useEffect(() => {
    if (errorSB) {
      const timer = setTimeout(() => {
        setErrorSB(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [errorSB]);
  
  const resetTriedDevices = useCallback(() => {
    triedDevicesRef.current = new Set();
  }, []);

  const fetchVideoDevices = useCallback(async () => {
    if (typeof navigator === "undefined" || !navigator.mediaDevices) {
      throw new Error("Camera API not supported in this browser.");
    }

    const enumerateVideoInputs = async () => {
      const devices = await navigator.mediaDevices.enumerateDevices();
      return devices.filter((device) => device.kind === "videoinput");
    };

    let devices = await enumerateVideoInputs();

    if (!devices.length) {
      throw new Error("No camera devices found.");
    }

    if (devices.every((device) => !device.label)) {
      let tempStream;
      try {
        tempStream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: false,
        });
        devices = await enumerateVideoInputs();
      } catch (permissionError) {
        console.error("Camera request failed while enumerating devices:", permissionError);

        const errorName = permissionError?.name;

        if (errorName === "NotAllowedError" || errorName === "PermissionDeniedError") {
          throw new Error("Camera permission was denied. Please allow camera access and try again.");
        }

        if (errorName === "NotReadableError") {
          throw new Error(
            "Your camera appears to be in use by another application. Please close any other app that might be using the camera and try again."
          );
        }

        if (errorName === "OverconstrainedError") {
          throw new Error(
            "No camera could satisfy the requested settings. Try lowering the camera quality or switching devices."
          );
        }

        if (errorName === "AbortError") {
          throw new Error(
            "The camera took too long to start. Please try again or switch to a different device."
          );
        }

        if (permissionError?.message) {
          throw new Error(permissionError.message);
        }

        throw permissionError;
      } finally {
        if (tempStream) {
          tempStream.getTracks().forEach((track) => track.stop());
        }
      }

      devices = devices.filter((device) => device.kind === "videoinput");

      if (!devices.length) {
        throw new Error("No camera devices found.");
      }
    }

    return devices;
  }, []);

  const selectDeviceIdForMode = useCallback((devices, mode, { preferUntried = false } = {}) => {
    if (!devices?.length) {
      return null;
    }

    const labelPattern =
      mode === "user"
        ? /(front|user|face|webcam)/i
        : /(back|rear|environment|world)/i;

    const consideredDevices = preferUntried
      ? devices.filter((device) => !triedDevicesRef.current.has(device.deviceId))
      : devices;

    const labeledMatch = consideredDevices.find((device) => labelPattern.test(device.label));

    if (labeledMatch) {
      return labeledMatch.deviceId;
    }

    const untriedFallback = devices.find((device) => !triedDevicesRef.current.has(device.deviceId));
    if (untriedFallback) {
      return untriedFallback.deviceId;
    }

    return consideredDevices[0]?.deviceId || devices[0]?.deviceId || null;
  }, []);

  const setActiveDevice = useCallback((deviceId) => {
    if (!deviceId) {
      return;
    }
    triedDevicesRef.current.add(deviceId);
    setSelectedDeviceId(deviceId);
    console.log("Setting active camera device", {
      deviceId,
      triedDevices: Array.from(triedDevicesRef.current),
    });
  }, []);

  const stopCameraStream = useCallback(() => {
    const stream = webcamRef.current?.stream || webcamRef.current?.video?.srcObject;
    if (stream) {
      const tracks = stream.getTracks ? stream.getTracks() : [];
      tracks.forEach((track) => track.stop());
    }
    if (webcamRef.current) {
      if (webcamRef.current.video) {
        webcamRef.current.video.srcObject = null;
      }
      webcamRef.current.stream = null;
    }
  }, []);
  const user_id = useSelector((state) =>
    state.session.setUserId ? state.session.setUserId.value : null
  );
  const sessionId = useSelector((state) => state.session.sessionId);

  // Handle image dialog open/close
  const handleImageClick = () => {
    setOpenImageDialog(true);
  };

  const handleCloseImageDialog = () => {
    setOpenImageDialog(false);
    if (cameraDialogOpen) {
      handleCloseCamera();
    }
  };

  const handleTriggerUpload = () => {
    if (isUploading) return;
    const uploadInput = document.getElementById("upload-image-input");
    if (uploadInput) {
      uploadInput.click();
    }
  };

  const handleRemoveProfileImage = async () => {
    if (isUploading) return;
    setIsUploading(true);
    setError(null);

    try {
      const searchData = {
        session: sessionId,
        module_name: "Notes",
        query: `notes.parent_id = '${user_id}' AND notes.name = 'Profile Photo'`,
        order_by: "",
        offset: 0,
        select_fields: ["id"],
        max_results: 20,
      };

      const existingNote = await callSuiteCrmApi("get_entry_list", searchData);

      if (existingNote?.entry_list?.length) {
        for (const note of existingNote.entry_list) {
          const noteId = note.id || note?.name_value_list?.id?.value;
          if (!noteId) continue;

          const deleteNoteData = {
            session: sessionId,
            module_name: "Notes",
            name_value_list: [
              { name: "id", value: noteId },
              { name: "deleted", value: 1 },
            ],
          };

          await callSuiteCrmApi("set_entry", deleteNoteData);
        }
      }

      setProfileImage(REMOVED_IMAGE_SENTINEL);
      dispatch(setUserPhoto(null));
      setOpenImageDialog(false);
      setSnackbarMessage("Profile image removed successfully!");
      setSuccessSB(true);
    } catch (removeError) {
      console.error("Failed to remove profile image:", removeError);
      setError(removeError.message || "Failed to remove profile image");
    } finally {
      setIsUploading(false);
    }
  };

  useEffect(() => {
    return () => {
      stopCameraStream();
    };
  }, [stopCameraStream]);

  const userPhoto = useSelector((state) =>
    state.session.setUserPhoto ? state.session.setUserPhoto : null
  );

  useEffect(() => {
    if (userPhoto) {
      setProfileImage(userPhoto);
    }
  }, [userPhoto]);

  const callSuiteCrmApi = async (method, data, retries = 2) => {
    const apiUrl = "https://seltentechnologiesdemo.com/moona-backend/service/v4_1/rest.php";

    const params = new URLSearchParams();
    params.append("method", method);
    params.append("input_type", "JSON");
    params.append("response_type", "JSON");
    params.append("rest_data", JSON.stringify(data));

    try {
      const response = await axios.post(apiUrl, params, {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
      });
      return response.data;
    } catch (error) {
      if (retries > 0) {
        await new Promise((resolve) => setTimeout(resolve, 1000));
        return callSuiteCrmApi(method, data, retries - 1);
      }
      throw error;
    }
  };

  const toBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result.split(",")[1]);
      reader.onerror = (error) => reject(error);
    });

  const uploadProfileImage = useCallback(
    async (file, { closeImageDialog = false, notify = true } = {}) => {
      if (!file) {
        return false;
      }

      setIsUploading(true);
      setError(null);

      try {
        if (!file.type.match(/image.*/)) {
          throw new Error("Only image files are allowed");
        }
        if (file.size > 15 * 1024 * 1024) {
          throw new Error("File size must be less than 15MB");
        }

        const searchData = {
          session: sessionId,
          module_name: "Notes",
          query: `notes.parent_id = '${user_id}' AND notes.name = 'Profile Photo'`,
          order_by: "",
          offset: 0,
          select_fields: ["id"],
          max_results: 1,
        };

        const existingNote = await callSuiteCrmApi("get_entry_list", searchData);
        let noteId;

        if (existingNote?.entry_list?.length) {
          noteId = existingNote.entry_list[0].id;
        } else {
          const noteData = {
            session: sessionId,
            module_name: "Notes",
            name_value_list: [
              { name: "name", value: "Profile Photo" },
              { name: "parent_type", value: "Users" },
              { name: "parent_id", value: user_id },
            ],
          };

          const noteResponse = await callSuiteCrmApi("set_entry", noteData);
          if (!noteResponse?.id) {
            throw new Error("Failed to create notes record");
          }
          noteId = noteResponse.id;
        }

        const fileData = await toBase64(file);
        const fileName = generateSafeFilename(file.name);

        const uploadData = {
          session: sessionId,
          note: {
            id: noteId,
            filename: fileName,
            file: fileData,
            file_mime_type: file.type,
            related_module_name: "Users",
            related_module_id: user_id,
          },
        };

        await callSuiteCrmApi("set_note_attachment", uploadData);

        const previewUrl = await new Promise((resolve, reject) => {
          const previewReader = new FileReader();
          previewReader.onload = () => resolve(previewReader.result);
          previewReader.onerror = (previewError) => reject(previewError);
          previewReader.readAsDataURL(file);
        });

        setProfileImage(previewUrl);
        dispatch(setUserPhoto(`data:${file.type};base64,${fileData}`));

        if (closeImageDialog) {
          setOpenImageDialog(false);
        }

        if (notify) {
          setSnackbarMessage("Profile image updated successfully!");
          setSuccessSB(true);
        }

        return true;
      } catch (error) {
        console.error("Upload failed:", error);
        setError(error.message || "Failed to upload profile image");
        return false;
      } finally {
        setIsUploading(false);
      }
    },
    [sessionId, user_id, dispatch, callSuiteCrmApi]
  );

  const handleUploadImage = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    await uploadProfileImage(file);
  };

  const generateSafeFilename = (filename) => {
    return filename.replace(/[^a-zA-Z0-9_.-]/g, "_");
  };

  const handleOpenCamera = useCallback(async () => {
    stopCameraStream();
    setCapturedImage(null);
    setCameraReady(false);
    setCameraError(null);
    setCameraMessage("Requesting camera access...");
    setCameraRetryCount(0);
    resetTriedDevices();
    setWebcamKey(-1);
    setLoosenConstraints(false);
    setForceDefaultDevice(false);
    setUseBareConstraints(false);
    setCameraDialogOpen(true);

    await new Promise((resolve) => setTimeout(resolve, 500));

    try {
      const devices = await fetchVideoDevices();
      setVideoDevices(devices);
      const hasSingleDevice = devices.length === 1;
      if (hasSingleDevice) {
        setForceDefaultDevice(true);
      }
      const deviceId =
        selectDeviceIdForMode(devices, facingMode) || devices[0]?.deviceId || null;

      if (!deviceId) {
        throw new Error("No camera devices found.");
      }

      setActiveDevice(deviceId);
      setCameraMessage("Initializing camera...");

      await new Promise((resolve) => setTimeout(resolve, 500));
      setWebcamKey((prev) => prev + 1);
    } catch (initError) {
      console.error("Camera initialization error:", initError);
      setCameraMessage(null);
      setCameraError(initError.message || "Unable to access camera. Please check permissions.");
    }
  }, [stopCameraStream, resetTriedDevices, fetchVideoDevices, selectDeviceIdForMode, facingMode, setActiveDevice]);

  const handleCloseCamera = useCallback(() => {
    setCameraDialogOpen(false);
    setCapturedImage(null);
    setCameraReady(false);
    setCameraError(null);
    setCameraMessage(null);
    setSelectedDeviceId(null);
    setCameraRetryCount(0);
    resetTriedDevices();
    setWebcamKey(-1);
    setLoosenConstraints(false);
    setForceDefaultDevice(false);
    setUseBareConstraints(false);
    stopCameraStream();
  }, [resetTriedDevices, stopCameraStream]);

  const capturePhoto = () => {
    const imageSrc = webcamRef.current?.getScreenshot();
    setCapturedImage(imageSrc || null);
  };

  const handleConfirmPhoto = useCallback(async () => {
    if (!capturedImage) {
      return;
    }

    try {
      const response = await fetch(capturedImage);
      const blob = await response.blob();
      const mimeType = blob.type || "image/jpeg";
      const file = new File([blob], "captured-profile-photo.jpg", { type: mimeType });
      const success = await uploadProfileImage(file);
      if (success) {
        setCameraDialogOpen(false);
        setCameraMessage(null);
        setCapturedImage(null);
        stopCameraStream();
      }
    } catch (confirmError) {
      console.error("Failed to process captured image:", confirmError);
      setError(confirmError.message || "Failed to process captured image");
    }
  }, [capturedImage, uploadProfileImage, stopCameraStream]);

  const handleRetakePhoto = () => {
    setCapturedImage(null);
    setCameraMessage(null);
  };

  const switchCamera = useCallback(async () => {
    stopCameraStream();
    setCameraReady(false);
    setCameraError(null);
    setCameraRetryCount(0);
    setWebcamKey(-1);
    setLoosenConstraints(false);
    setForceDefaultDevice(false);
    setUseBareConstraints(false);

    await new Promise((resolve) => setTimeout(resolve, 600));

    try {
      const devices = videoDevices.length ? videoDevices : await fetchVideoDevices();
      if (!videoDevices.length) {
        setVideoDevices(devices);
      }

      if (devices.length <= 1) {
        setFacingMode("user");
        setForceDefaultDevice(true);
        setCameraMessage("Only one camera detected on this device.");
        setWebcamKey((prev) => prev + 1);
        return;
      }

      const nextMode = facingMode === "user" ? "environment" : "user";
      setFacingMode(nextMode);
      setCameraMessage(`Switching to ${nextMode === "user" ? "front" : "rear"} camera...`);

      resetTriedDevices();
      const nextDeviceId =
        selectDeviceIdForMode(devices, nextMode) || devices[0]?.deviceId || null;

      if (!nextDeviceId) {
        throw new Error("No camera devices found.");
      }

      setActiveDevice(nextDeviceId);

      await new Promise((resolve) => setTimeout(resolve, 500));
      setWebcamKey((prev) => prev + 1);
    } catch (switchError) {
      console.error("Switch camera failed:", switchError);
      setCameraMessage(null);
      setCameraError(switchError.message || "Unable to switch camera. Please try again.");
    }
  }, [stopCameraStream, videoDevices, fetchVideoDevices, facingMode, resetTriedDevices, selectDeviceIdForMode, setActiveDevice]);

  const retryCamera = useCallback(async () => {
    stopCameraStream();
    setCameraError(null);
    setCameraReady(false);
    setCameraMessage("Retrying camera...");
    setCameraRetryCount(0);
    resetTriedDevices();
    setWebcamKey(-1);
    setLoosenConstraints(false);
    setForceDefaultDevice(false);
    setUseBareConstraints(false);

    await new Promise((resolve) => setTimeout(resolve, 700));

    try {
      const devices = videoDevices.length ? videoDevices : await fetchVideoDevices();
      if (!videoDevices.length) {
        setVideoDevices(devices);
      }

      const hasSingleDevice = devices.length === 1;
      setForceDefaultDevice(hasSingleDevice);

      const deviceId =
        selectDeviceIdForMode(devices, facingMode) || devices[0]?.deviceId || null;

      if (!deviceId) {
        throw new Error("No camera devices found.");
      }

      setActiveDevice(deviceId);

      await new Promise((resolve) => setTimeout(resolve, 500));
      setWebcamKey((prev) => prev + 1);
    } catch (retryError) {
      console.error("Retry camera failed:", retryError);
      setCameraMessage(null);
      setCameraError(retryError.message || "Unable to access camera. Please check permissions.");
    }
  }, [stopCameraStream, videoDevices, fetchVideoDevices, resetTriedDevices, selectDeviceIdForMode, facingMode, setActiveDevice]);

  const handleCameraReady = useCallback(() => {
    console.log("Camera ready");
    setCameraReady(true);
    setCameraError(null);
    setCameraMessage(null);
    setCameraRetryCount(0);
    setLoosenConstraints(false);
    setForceDefaultDevice(false);
    setUseBareConstraints(false);
  }, []);

  const handleCameraError = useCallback(async (error) => {
    console.error("Camera error:", error);
    console.error("Camera error details", {
      name: error?.name,
      message: error?.message,
      code: error?.code,
      facingMode,
      selectedDeviceId,
      loosenConstraints,
      triedDevices: Array.from(triedDevicesRef.current),
      retryCount: cameraRetryCount,
      timestamp: new Date().toISOString(),
    });
    stopCameraStream();
    setCameraReady(false);
    setCameraError(null);
    setWebcamKey(-1);

    if (error?.name === "NotAllowedError" || error?.name === "PermissionDeniedError") {
      setCameraMessage(null);
      setCameraError("Camera permission was denied. Please allow camera access in your browser settings and try again.");
      return;
    }

    if (error?.name === "NotFoundError") {
      setCameraMessage(null);
      setCameraError("No camera devices were found. Please connect a camera and try again.");
      return;
    }

    if (error?.name === "NotReadableError") {
      setCameraMessage(null);
      setCameraError("Your camera appears to be in use by another application. Please close any app using the camera and try again.");
      return;
    }

    if (error?.name === "OverconstrainedError") {
      setCameraMessage(null);
      setCameraError("The selected camera cannot satisfy the requested settings. Try switching cameras or close other apps that may be reserving it.");
      return;
    }

    let devices = videoDevices;

    if (!devices.length) {
      try {
        devices = await fetchVideoDevices();
        setVideoDevices(devices);
      } catch (deviceError) {
        setCameraMessage(null);
        setCameraError(deviceError.message || "Unable to access camera. Please check permissions.");
        return;
      }
    }

    if (error?.name === "AbortError") {
      await new Promise((resolve) => setTimeout(resolve, 600));

      const scheduleReinitialization = (delay = 900) => {
        setTimeout(() => {
          stopCameraStream();
          setCameraError(null);
          setCameraReady(false);
          setWebcamKey((prev) => prev + 1);
        }, delay);
      };

      const alternateDeviceId = selectDeviceIdForMode(devices, facingMode, { preferUntried: true });

      if (alternateDeviceId && !triedDevicesRef.current.has(alternateDeviceId)) {
        setCameraMessage("Trying another camera device...");
        setActiveDevice(alternateDeviceId);
        await new Promise((resolve) => setTimeout(resolve, 500));
        setWebcamKey((prev) => prev + 1);
        return;
      }

      if (facingMode === "environment") {
        const frontDeviceId =
          selectDeviceIdForMode(devices, "user", { preferUntried: true }) ||
          selectDeviceIdForMode(devices, "user");

        if (frontDeviceId && !triedDevicesRef.current.has(frontDeviceId)) {
          setCameraMessage("Rear camera not available. Switching to front camera...");
          resetTriedDevices();
          setActiveDevice(frontDeviceId);
          setFacingMode("user");
          await new Promise((resolve) => setTimeout(resolve, 500));
          setWebcamKey((prev) => prev + 1);
          return;
        }
      }

      if (!loosenConstraints) {
        setCameraMessage("Camera timed out. Retrying with lower video quality...");
        setLoosenConstraints(true);
        setCameraRetryCount((prev) => Math.min(prev + 1, MAX_CAMERA_RETRIES));
        scheduleReinitialization();
        return;
      }

      if (!forceDefaultDevice) {
        setCameraMessage("Camera timed out. Switching to browser default camera...");
        setForceDefaultDevice(true);
        resetTriedDevices();
        setSelectedDeviceId(null);
        setFacingMode("user");
        setCameraRetryCount((prev) => Math.min(prev + 1, MAX_CAMERA_RETRIES));
        scheduleReinitialization();
        return;
      }

      if (!useBareConstraints) {
        setCameraMessage("Camera timed out. Retrying with minimal settings...");
        setUseBareConstraints(true);
        setCameraRetryCount((prev) => Math.min(prev + 1, MAX_CAMERA_RETRIES));
        scheduleReinitialization(1000);
        return;
      }

      if (cameraRetryCount + 1 <= MAX_CAMERA_RETRIES) {
        setCameraMessage("Camera still loading. Retrying...");
        setCameraRetryCount((prev) => Math.min(prev + 1, MAX_CAMERA_RETRIES));
        scheduleReinitialization();
        return;
      }

      setCameraMessage(null);
      setCameraError("Camera initialization timed out. Please try again or check your camera permissions.");
      return;
    }

    setCameraMessage(null);
    setCameraError("Unable to access camera. Please check permissions.");
  }, [videoDevices, fetchVideoDevices, selectDeviceIdForMode, facingMode, cameraRetryCount, stopCameraStream, setActiveDevice, resetTriedDevices, loosenConstraints, selectedDeviceId, forceDefaultDevice, useBareConstraints]);

  const videoConstraints = useMemo(() => {
    if (useBareConstraints) {
      return null;
    }

    const baseConstraints = loosenConstraints
      ? {
          width: { ideal: 640, min: 320 },
          height: { ideal: 480, min: 240 },
          frameRate: { ideal: 24, min: 10 },
        }
      : {
          width: { ideal: 640 },
          height: { ideal: 480 },
          frameRate: { ideal: 30 },
        };

    if (forceDefaultDevice) {
      return baseConstraints;
    }

    if (selectedDeviceId) {
      return {
        ...baseConstraints,
        deviceId: { ideal: selectedDeviceId },
      };
    }

    return {
      ...baseConstraints,
      facingMode: { ideal: facingMode },
    };
  }, [facingMode, selectedDeviceId, forceDefaultDevice, loosenConstraints, useBareConstraints]);

  const handleEditClick = () => {
    if (onEditClick) {
      onEditClick();
    } else {
      navigate("/profile/editProfile", { state: { profile } });
    }
  };

  const handleOpenShare = () => setShareOpen(true);
  const handleCloseShare = () => setShareOpen(false);

  const handleShareResult = (result) => {
    // result: { type, fields, success, url?, message? }
    if (!result) return;
    if (result.success) {
      // Show only concise confirmations — do not display copied content or URLs
      if (result.type === "link") {
        setSnackbarMessage("Profile link copied to clipboard");
      } else if (result.type === "text") {
        setSnackbarMessage("Selected profile data copied to clipboard");
      } else if (result.type === "pdf") {
        setSnackbarMessage("PDF generated — use the Download button to save the file.");
      } else {
        setSnackbarMessage("Share action completed");
      }
      setSuccessSB(true);
    } else {
      setSnackbarMessage(result.message || "Share failed");
      setErrorSB(true);
    }
  };

  const displayImage =
    profileImage === REMOVED_IMAGE_SENTINEL
      ? null
      : profileImage ?? profile?.photo_url;

  return (
    <MDBox position="relative" mb={5}>
      <Card
        sx={{
          position: "sticky",
          top: (theme) => theme.spacing(12),
          zIndex: (theme) => theme.zIndex.appBar - 1,
          px: { xs: 2, md: 3 },
          py: { xs: 3, md: 4 },
        }}
      >
        {/* Edit Button - Top Right */}
        <MDBox
          position="absolute"
          top={16}
          right={16}
          sx={{
            zIndex: 10,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Button
            variant="contained"
            size="medium"
            startIcon={<FaEdit />}
            onClick={handleEditClick}
            disabled={isUploading}
            sx={{
              fontWeight: 600,
              color: "#fff",
              backgroundColor: "#0071a8",
              boxShadow: 2,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "8px 16px",
              '&:hover': {
                backgroundColor: "#005f8c",
                boxShadow: 4,
              },
              '&.Mui-disabled': {
                backgroundColor: "rgba(0, 113, 168, 0.4)",
                color: "#fff",
              },
              '& .MuiButton-startIcon': {
                display: 'flex',
                alignItems: 'center',
                marginRight: '8px',
              },
            }}
          >
            Edit Profile
          </Button>
          <Button
            variant="outlined"
            size="medium"
            startIcon={<FaShare />}
            onClick={handleOpenShare}
            sx={{
              fontWeight: 600,
              color: (theme) => theme.palette.text.primary,
              borderColor: (theme) => theme.palette.divider,
              backgroundColor: "transparent",
              padding: "8px 14px",
              '&:hover': {
                backgroundColor: (theme) => theme.palette.action.hover,
                boxShadow: 1,
              },
              '& .MuiButton-startIcon': {
                display: 'flex',
                alignItems: 'center',
                marginRight: '8px',
              },
            }}
          >
            Share
          </Button>
        </MDBox>

        <Grid container spacing={2} alignItems="center">
          <Grid item sx={{ width: "100%" }}>
            <MDBox
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              position="relative"
              gap={2}
              sx={{ width: "100%", textAlign: "center", px: { xs: 0, md: 0 } }}
            >
              {/* Profile Image - Centered and Larger */}
              <MDAvatar
                src={displayImage || burceMars}
                alt="profile-image"
                size="xxl"
                shadow="lg"
                onClick={handleImageClick}
                style={{ cursor: "pointer", width: 180, height: 180 }}
              />
              <input
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={handleUploadImage}
                id="upload-image-input"
                disabled={isUploading}
              />
              
              {/* User Name - Centered Below Image */}
              <Typography variant="h4" sx={{ fontWeight: 600, pt: 1 }}>
                {profile?.first_name?.value || profile?.user_name?.value || "User"} {profile?.last_name?.value || ""}
              </Typography>
              {/* Family Group ID - show below name if available */}
              <Typography variant="body2" sx={{ color: "#6b7280", mt: 0.5 }}>
                {`Family Group ID: ${profile?.family_group_id_c?.value || profile?.family_group_id_c || profile?.family_group_id || "N/A"}`}
              </Typography>
            </MDBox>
          </Grid>
        </Grid>
        {error && (
          <MDBox p={2} color="error">
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          </MDBox>
        )}
        {children}

  {/* Share Profile Dialog */}
        <ShareProfileDialog
          open={shareOpen}
          onClose={handleCloseShare}
          profile={profile}
          onShare={handleShareResult}
          educationEntries={educationDetails}
          workEntries={workDetails}
          idCardEntries={idCardDetails}
          bankEntries={bankDetails}
          addressEntries={addressDetails}
        />

  {/* Image Preview Dialog */}
        <Dialog open={openImageDialog} onClose={handleCloseImageDialog} maxWidth="md" fullWidth>
          <DialogTitle sx={{ textAlign: "center", fontWeight: 600 }}>
            Profile Photo
            <IconButton
              aria-label="close"
              onClick={handleCloseImageDialog}
              sx={{
                position: "absolute",
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ textAlign: "center", padding: "20px" }}>
            {displayImage ? (
              <img
                src={displayImage}
                alt="Profile Preview"
                style={{
                  maxWidth: "100%",
                  maxHeight: "70vh",
                  borderRadius: "8px",
                }}
              />
            ) : (
              <Typography variant="body2" color="text.secondary">
                No profile image uploaded yet.
              </Typography>
            )}
            <MDBox mt={3} display="flex" justifyContent="center" gap={2} flexWrap="wrap">
              <Button
                variant="contained"
                color="primary"
                onClick={handleTriggerUpload}
                disabled={isUploading}
                sx={{
                  color: "#fff",
                  fontWeight: 600,
                  backgroundColor: "#1976d2",
                  '&:hover': {
                    backgroundColor: "#115293",
                  },
                }}
              >
                {isUploading ? "Uploading..." : "Change Image"}
              </Button>
              <Button
                variant="contained"
                color="success"
                onClick={handleOpenCamera}
                disabled={isUploading}
                startIcon={<CameraAltIcon />}
                sx={{
                  color: "#fff",
                  fontWeight: 600,
                  backgroundColor: "#2e7d32",
                  '&:hover': {
                    backgroundColor: "#1b5e20",
                  },
                }}
              >
                Use Camera
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={handleRemoveProfileImage}
                disabled={isUploading || !displayImage}
                sx={{
                  backgroundColor: "#d32f2f",
                  color: "#fff",
                  fontWeight: 600,
                  '&:hover': {
                    backgroundColor: "#c62828",
                  },
                  '&.Mui-disabled': {
                    backgroundColor: "rgba(211, 47, 47, 0.4)",
                    color: "#fff",
                  },
                }}
              >
                Remove Image
              </Button>
            </MDBox>
          </DialogContent>
        </Dialog>

        <Dialog open={cameraDialogOpen} onClose={handleCloseCamera} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ fontWeight: 600 }}>
            Take Photo
            <IconButton
              aria-label="close"
              onClick={handleCloseCamera}
              sx={{
                position: "absolute",
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ textAlign: "center", padding: "20px" }}>
            {!capturedImage ? (
              <Box sx={{ position: "relative", display: "inline-block" }}>
                {cameraError ? (
                  <Box
                    sx={{
                      width: 400,
                      height: 300,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      border: "2px dashed #ccc",
                      borderRadius: "8px",
                      backgroundColor: "#f5f5f5",
                      px: 2,
                    }}
                  >
                    <Typography variant="h6" color="error" gutterBottom>
                      Camera Error
                    </Typography>
                    <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mb: 2 }}>
                      {cameraError}
                    </Typography>
                    <Button
                      variant="contained"
                      onClick={retryCamera}
                      startIcon={<CameraAltIcon />}
                      sx={{
                        fontWeight: 600,
                        color: "#fff",
                        backgroundColor: "#1976d2",
                        '&:hover': { backgroundColor: "#115293" },
                      }}
                    >
                      Retry Camera
                    </Button>
                  </Box>
                ) : webcamKey < 0 ? (
                  <Box
                    sx={{
                      width: 400,
                      height: 300,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      border: "2px dashed #ccc",
                      borderRadius: "8px",
                      backgroundColor: "#f5f5f5",
                    }}
                  >
                    <Typography variant="body1" color="text.secondary">
                      {cameraMessage || "Preparing camera..."}
                    </Typography>
                  </Box>
                ) : (
                  <Webcam
                    key={`webcam-${webcamKey}-${facingMode}-${selectedDeviceId || "default"}`}
                    audio={false}
                    ref={webcamRef}
                    screenshotFormat="image/jpeg"
                    screenshotQuality={0.92}
                    width={400}
                    height={300}
                    style={{ borderRadius: "8px", objectFit: "cover" }}
                    videoConstraints={videoConstraints ?? undefined}
                    mirrored={facingMode === "user"}
                    onUserMediaError={handleCameraError}
                    onUserMedia={handleCameraReady}
                  />
                )}
                {!cameraReady && !cameraError && webcamKey >= 0 && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      color: "white",
                      fontSize: "16px",
                      fontWeight: "bold",
                      textShadow: "2px 2px 4px rgba(0,0,0,0.5)",
                      zIndex: 1,
                    }}
                  >
                    {cameraMessage || "Loading camera..."}
                  </Box>
                )}
                <Box sx={{ mt: 2, display: "flex", justifyContent: "center", gap: 2, flexWrap: "wrap" }}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={capturePhoto}
                    disabled={!cameraReady || !!cameraError}
                    sx={{ fontWeight: 600, color: "#fff" }}
                  >
                    Capture Photo
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={switchCamera}
                    startIcon={<CameraAltIcon />}
                    disabled={!cameraReady || !!cameraError || videoDevices.length <= 1}
                    sx={{
                      fontWeight: 600,
                      color: "#1976d2",
                      borderColor: "#1976d2",
                      '&:hover': {
                        borderColor: "#115293",
                        color: "#115293",
                      },
                    }}
                  >
                    Switch Camera
                  </Button>
                </Box>
                {cameraMessage && cameraReady && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {cameraMessage}
                  </Typography>
                )}
              </Box>
            ) : (
              <Box>
                <img
                  src={capturedImage}
                  alt="Captured"
                  style={{
                    maxWidth: "100%",
                    maxHeight: "300px",
                    borderRadius: "8px",
                  }}
                />
                <Box sx={{ mt: 2, display: "flex", justifyContent: "center", gap: 2, flexWrap: "wrap" }}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleConfirmPhoto}
                    sx={{ fontWeight: 600, color: "#fff" }}
                  >
                    Confirm
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleRetakePhoto}
                    sx={{
                      fontWeight: 600,
                      color: "#1976d2",
                      borderColor: "#1976d2",
                      '&:hover': {
                        borderColor: "#115293",
                        color: "#115293",
                      },
                    }}
                  >
                    Retake
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={handleCloseCamera}
                    sx={{
                      fontWeight: 600,
                      color: "#d32f2f",
                      borderColor: "#d32f2f",
                      '&:hover': {
                        borderColor: "#b71c1c",
                        color: "#b71c1c",
                      },
                    }}
                  >
                    Cancel
                  </Button>
                </Box>
              </Box>
            )}
          </DialogContent>
        </Dialog>
      </Card>

      {/* Success Snackbar */}
      <MDSnackbar
        color="success"
        icon="check"
        title="Success"
        content={snackbarMessage}
        dateTime="just now"
        open={successSB}
        onClose={() => setSuccessSB(false)}
        close={() => setSuccessSB(false)}
        bgWhite
      />

      {/* Error Snackbar */}
      <MDSnackbar
        color="error"
        icon="warning"
        title="Error"
        content={snackbarMessage}
        dateTime="just now"
        open={errorSB}
        onClose={() => setErrorSB(false)}
        close={() => setErrorSB(false)}
        bgWhite
      />
    </MDBox>
  );
}

Header.defaultProps = {
  children: "",
  profile: {},
  onEditClick: null,
  educationDetails: [],
  workDetails: [],
  idCardDetails: [],
  bankDetails: [],
  addressDetails: [],
};

Header.propTypes = {
  children: PropTypes.node,
  profile: PropTypes.object,
  onEditClick: PropTypes.func,
  educationDetails: PropTypes.array,
  workDetails: PropTypes.array,
  idCardDetails: PropTypes.array,
  bankDetails: PropTypes.array,
  addressDetails: PropTypes.array,
};

export default Header;
